import React, { useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  alpha,
  lighten,
  useTheme,
} from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';

import EmployeeName from '../../../components/EmployeeName';
import { COLUMNS } from './constants';
import EmptyRow from './EmptyRow';
import IdCardIndicator from './IdCardIndicator';
import NumCell from './NumCell';
import SectionHeader from './SectionHeader';

export default function EmployeeGroupTable({ title, rows, accentColor }) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState({});

  const toggle = (employeeId) => {
    setExpanded((prev) => ({ ...prev, [employeeId]: !prev[employeeId] }));
  };

  return (
    <Box sx={{ mb: 3.5 }}>
      <SectionHeader accentColor={accentColor} title={title} count={rows.length} />
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: lighten(accentColor, 0.88), width: 40 }} />
              <TableCell sx={{ bgcolor: lighten(accentColor, 0.88), fontWeight: 700 }}>ชื่อ</TableCell>
              {COLUMNS.map((c) => (
                <TableCell key={c.key} align="right" sx={{ bgcolor: lighten(accentColor, 0.88), fontWeight: 700, whiteSpace: 'nowrap' }}>
                  {c.label}
                </TableCell>
              ))}
              <TableCell align="right" sx={{ bgcolor: lighten(accentColor, 0.8), fontWeight: 700, whiteSpace: 'nowrap' }}>
                สุทธิ
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <EmptyRow colSpan={COLUMNS.length + 3} icon={<EventBusyIcon />} text="ไม่มีข้อมูล" />
            )}
            {rows.map((row, idx) => (
              <React.Fragment key={row.employee}>
                <TableRow hover sx={{ bgcolor: idx % 2 === 1 ? alpha(theme.palette.text.primary, 0.02) : 'transparent' }}>
                  <TableCell>
                    <IconButton size="small" onClick={() => toggle(row.employee)}>
                      {expanded[row.employee] ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IdCardIndicator hasIdCard={row.has_id_card} />
                      <Box>
                        <Typography variant="body2" component="div">
                          <EmployeeName id={row.employee} name={row.name} />
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{row.department} {row.role}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  {COLUMNS.map((c) => (
                    <NumCell key={c.key} value={row[c.key]} />
                  ))}
                  <NumCell value={row.total} emphasize />
                </TableRow>
                <TableRow>
                  <TableCell colSpan={COLUMNS.length + 3} sx={{ p: 0, borderBottom: expanded[row.employee] ? undefined : 'none' }}>
                    <Collapse in={!!expanded[row.employee]} timeout="auto" unmountOnExit>
                      <Box
                        sx={{
                          m: 1.5,
                          borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderLeft: `3px solid ${alpha(accentColor, 0.5)}`,
                          overflow: 'hidden',
                        }}
                      >
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.03) }}>
                              <TableCell sx={{ fontWeight: 600 }}>งวด</TableCell>
                              {COLUMNS.map((c) => (
                                <TableCell key={c.key} align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{c.label}</TableCell>
                              ))}
                              <TableCell align="right" sx={{ fontWeight: 600 }}>สุทธิ</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {row.periods.map((p) => (
                              <TableRow key={p.salaryListId}>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  {dayjs(p.dateStart).format('DD/MM/BBBB')} - {dayjs(p.dateEnd).format('DD/MM/BBBB')}
                                </TableCell>
                                {COLUMNS.map((c) => (
                                  <NumCell key={c.key} value={p[c.key]} />
                                ))}
                                <NumCell value={p.total} emphasize />
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
